import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import StudioPlayer from "@/components/StudioPlayer";
import PosterCard, { Countdown, PosterTile, DIVISION_META } from "@/components/Studio";
import { NotifyForm } from "@/components/ProductCard";
import { ShareButtons } from "@/components/Forms";
import { api, assetUrl } from "@/lib/api";
import NotFound from "../NotFound";

function Credit({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="font-ui text-[10px] uppercase tracking-[0.25em] text-paper/40 mb-1">{label}</p>
      <p className="font-ui text-sm text-paper/85">{value}</p>
    </div>
  );
}

export default function Watch() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setV(null);
    setMissing(false);
    api.get(`/public/cinema/watch/${slug}`).then((r) => setV(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <NotFound />;
  if (!v) {
    return (
      <div className="bg-ink min-h-screen pt-40 px-6" data-testid="watch-loading">
        <p className="font-ui text-paper/40 text-xs uppercase tracking-[0.3em] text-center">Loading production…</p>
      </div>
    );
  }

  const episodes = v.episodes || [];
  const idx = episodes.findIndex((e) => e.slug === slug);
  const prevEp = idx > 0 ? episodes[idx - 1] : null;
  const nextEp = idx >= 0 && idx < episodes.length - 1 ? episodes[idx + 1] : null;

  return (
    <div className="bg-ink min-h-screen" data-testid="watch-page">
      <Seo title={`${v.title} — Dirty Church Chronicles Studios`}
        description={v.logline || v.synopsis || "A Dirty Church Chronicles Studios production."}
        path={`/cinema/watch/${v.slug}`}
        jsonLd={v.released ? {
          "@context": "https://schema.org", "@type": "VideoObject",
          name: v.title, description: v.logline || v.synopsis || "",
          ...(v.poster_url ? { thumbnailUrl: `https://dirtychurchchronicles.com${v.poster_url}` } : {}),
          ...(v.release_date ? { uploadDate: v.release_date } : {}),
        } : null} />

      <section className="pt-28 pb-10 max-w-6xl mx-auto px-6 sm:px-10">
        <Link to={v.series ? `/cinema/series/${v.series.slug}` : `/cinema/${v.division === "studio-ident" ? "" : v.division}`}
          data-testid="watch-back-link"
          className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] text-paper/50 hover:text-paper transition-colors mb-8">
          <ArrowLeft size={13} /> {v.series ? v.series.title : (DIVISION_META[v.division]?.title || "The Cinema")}
        </Link>

        {v.released ? (
          <Reveal>
            <div className="border border-paper/10 overflow-hidden shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]">
              <StudioPlayer video={v} ident={v.ident} />
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <div className="border border-gold/25 bg-coal p-10 sm:p-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-center" data-testid="watch-premiere-gate">
              <div className="md:col-span-4 aspect-video md:aspect-[4/3] border border-paper/10 overflow-hidden bg-black">
                <PosterTile video={v} />
              </div>
              <div className="md:col-span-8">
                <p className="eyebrow mb-4">Premiere</p>
                <p className="font-reading text-xl text-paper/70 mb-8 max-w-lg">
                  This production premieres at the scheduled time below. The screening unlocks automatically.
                </p>
                <Countdown releaseAt={v.release_at} timezone={v.timezone} />
                <div className="mt-8 max-w-md">
                  <NotifyForm product={{ product_id: `video:${v.slug}` }} compact />
                </div>
              </div>
            </div>
          </Reveal>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <Reveal>
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <span className="font-ui text-[10px] uppercase tracking-[0.25em] text-crimsonhi">
                {DIVISION_META[v.division]?.title || v.division}
              </span>
              {v.season != null && v.episode != null && (
                <span className="font-ui text-[10px] uppercase tracking-[0.25em] text-paper/45" data-testid="watch-episode-label">
                  Season {v.season} · Episode {v.episode}
                </span>
              )}
              {v.views > 0 && (
                <span className="font-ui text-[10px] uppercase tracking-[0.25em] text-paper/35">{v.views} views</span>
              )}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-5xl text-paper leading-tight mb-4" data-testid="watch-title">{v.title}</h1>
            {v.logline && <p className="font-reading italic text-xl text-gold/85 mb-6">{v.logline}</p>}
            <div className="flex gap-2.5 flex-wrap mb-8">
              {[v.runtime, v.release_date, v.genre, v.topic].filter(Boolean).map((x, i) => (
                <span key={i} className="font-ui text-xs text-paper/55 border border-paper/20 px-3.5 py-1.5 rounded-full">{x}</span>
              ))}
            </div>
            {v.synopsis && <p className="font-reading text-xl text-paper/75 leading-relaxed mb-8" data-testid="watch-synopsis">{v.synopsis}</p>}
            {v.advisory && (
              <p className="font-ui text-xs text-paper/50 border-l-2 border-crimson pl-4 mb-8" data-testid="watch-advisory">
                Content advisory: {v.advisory}
              </p>
            )}
            <div className="text-paper/60 mb-4">
              <ShareButtons title={v.title} text={v.logline || "A Dirty Church Chronicles Studios production."} path={`/cinema/watch/${v.slug}`} />
            </div>
          </Reveal>

          {(v.director || v.producer || v.writer || v.cast || v.copyright_owner) && (
            <Reveal delay={0.1}>
              <div className="border-t border-paper/10 pt-8 mt-4 grid grid-cols-2 sm:grid-cols-3 gap-6" data-testid="watch-credits">
                <Credit label="Director" value={v.director} />
                <Credit label="Producer" value={v.producer} />
                <Credit label="Writer" value={v.writer} />
                <Credit label="Cast" value={v.cast} />
                <Credit label="Copyright" value={v.copyright_owner} />
                <Credit label="Music" value={v.music_license} />
                <Credit label="Footage" value={v.footage_license} />
              </div>
            </Reveal>
          )}

          {(prevEp || nextEp) && (
            <Reveal delay={0.15}>
              <div className="flex justify-between gap-6 border-t border-paper/10 pt-8 mt-8" data-testid="episode-nav">
                {prevEp ? (
                  <Link to={`/cinema/watch/${prevEp.slug}`} data-testid="prev-episode-link"
                    className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] text-paper/60 hover:text-paper transition-colors">
                    <ArrowLeft size={13} /> E{prevEp.episode} — {prevEp.title}
                  </Link>
                ) : <span />}
                {nextEp && (
                  <Link to={`/cinema/watch/${nextEp.slug}`} data-testid="next-episode-link"
                    className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] text-paper/60 hover:text-paper transition-colors">
                    E{nextEp.episode} — {nextEp.title} <ArrowRight size={13} />
                  </Link>
                )}
              </div>
            </Reveal>
          )}
        </div>

        <div className="lg:col-span-4 space-y-6">
          {v.trailer && (
            <Reveal delay={0.1}>
              <Link to={`/cinema/watch/${v.trailer.slug}`} data-testid="watch-trailer-link"
                className="block border border-paper/10 bg-coal p-5 transition-colors duration-300 hover:border-crimson/50">
                <p className="eyebrow mb-3">Trailer</p>
                <p className="font-display text-lg text-paper">{v.trailer.title}</p>
              </Link>
            </Reveal>
          )}
          {v.related_excerpt && (
            <Reveal delay={0.15}>
              <Link to={`/chronicles/${v.related_excerpt}`} data-testid="watch-related-chronicle"
                className="block border border-gold/30 bg-coal p-5 transition-colors duration-300 hover:border-gold/60">
                <p className="eyebrow mb-3">The Chronicles in Motion</p>
                <p className="font-display text-lg text-paper inline-flex items-center gap-2">
                  <BookOpen size={16} /> Explore the chronicle that inspired this film
                </p>
              </Link>
            </Reveal>
          )}
          {v.related_product && (
            <Reveal delay={0.2}>
              <Link to="/bookstore/volume-one" data-testid="watch-related-book"
                className="block border border-paper/10 bg-coal p-5 transition-colors duration-300 hover:border-paper/30">
                <p className="eyebrow mb-3">From the Bookstore</p>
                <p className="font-display text-lg text-paper">Own Dirty Church Chronicles: Volume I</p>
              </Link>
            </Reveal>
          )}
        </div>
      </section>

      {(v.related || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-28" data-testid="watch-related-row">
          <h2 className="font-display font-bold text-2xl text-paper mb-6">Related Productions</h2>
          <div className="flex gap-5 overflow-x-auto pb-4">
            {v.related.map((r) => <PosterCard key={r.video_id} video={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
